import packageMetadata from "../package.json" with { type: "json" };
import type { GeneratedPaymentResult } from "./generated/public-contract-types.ts";

export type LockerversePaymentOption =
  | "one-time"
  | "monthly"
  | "annually"
  | "every-six-months";
export type LockerverseEnvironment = "development" | "production";
export type LockerverseFulfillmentType = "none" | "event_admission";

export const LOCKERVERSE_SDK_VERSION = packageMetadata.version;

export type LockerverseProduct = {
  id: string;
  listingId: string;
  slug: string;
  name: string;
  description: string | null;
  fulfillmentType: LockerverseFulfillmentType;
  pricingMode: "fixed" | "custom";
  paymentOption: LockerversePaymentOption;
  amountCents: number | null;
  minimumAmountCents: number | null;
  minimumQuantity: number | null;
  /** Per-order limit for this widget listing. Null means no per-order limit. */
  maximumQuantity: number | null;
  inventoryQuantity: number | null;
  acceptsDiscounts: boolean;
};

export type LockerverseAvailabilityStatus = "available" | "sold_out";

export type LockerverseCatalogAvailability = {
  listings: Array<{
    listingId: string;
    maxPurchasableQuantity: number | null;
    remainingQuantity: number | null;
    status: LockerverseAvailabilityStatus;
  }>;
  sharedLimits: Array<{
    id: string;
    listingIds: string[];
    remainingQuantity: number;
    status: LockerverseAvailabilityStatus;
  }>;
};

export type LockerverseCatalog = {
  availability: LockerverseCatalogAvailability;
  community: LockerverseCommunityBranding | null;
  event: LockerverseEvent | null;
  payment: LockerversePaymentConfiguration;
  pricingPolicy: LockerverseCheckoutPricingPolicy;
  products: LockerverseProduct[];
  widget: LockerverseWidgetConfiguration;
};

export type LockerverseEvent = {
  description: string | null;
  endAt: string;
  id: string;
  location: string | null;
  name: string;
  startAt: string;
  status: "draft" | "active" | "finished" | "cancelled";
};

export type LockerverseCommunityBranding = {
  widgetAchEnabled?: boolean | undefined;
  accentColor: string;
  imageUrl: string | null;
  memberIdLabel?: string | null | undefined;
  name: string;
};

export type LockerverseCheckoutPricingPolicy = {
  currency: "usd";
  serviceFeeRateBasisPoints: number;
};

export type LockerversePaymentConfiguration = {
  connectedAccountId: string | null;
  currency: "usd";
  publishableKey: string;
};

export type LockerverseWidgetFields = {
  memberId: boolean;
  merchSize: boolean;
  phone: boolean;
  promoCode: boolean;
  serviceFee: boolean;
  shipping: boolean;
  tipping: boolean;
};

export type LockerverseCustomFieldOption = { id: string; label: string };

type LockerverseCustomFieldBase = {
  id: string;
  label: string;
  paymentOptionIds?: string[] | undefined;
  required: boolean;
};

export type LockerverseTextCustomField = LockerverseCustomFieldBase & {
  type: "text" | "textarea";
};

export type LockerverseBooleanCustomField = LockerverseCustomFieldBase & {
  type: "checkbox";
};

export type LockerverseSingleOptionCustomField = LockerverseCustomFieldBase & {
  options: LockerverseCustomFieldOption[];
  type: "radio" | "select";
};

export type LockerverseMultiOptionCustomField = LockerverseCustomFieldBase & {
  options: LockerverseCustomFieldOption[];
  type: "checkboxes" | "multi_select";
};

export type LockerverseCustomField =
  | LockerverseBooleanCustomField
  | LockerverseTextCustomField
  | LockerverseSingleOptionCustomField
  | LockerverseMultiOptionCustomField;

export type LockerverseCustomFieldAnswerValue<
  Field extends LockerverseCustomField,
> = Field extends LockerverseBooleanCustomField
  ? boolean
  : Field extends LockerverseMultiOptionCustomField
    ? string[]
    : string;

export type LockerverseCustomFieldAnswer<
  Field extends LockerverseCustomField = LockerverseCustomField,
> = Field extends LockerverseCustomField
  ? { fieldId: Field["id"]; value: LockerverseCustomFieldAnswerValue<Field> }
  : never;

export type LockerverseWidgetConfiguration = {
  requiredShipping?:
    | {
        additionalUnitAmountCents?: number | undefined;
        productId: string;
        triggerProductIds: string[];
      }
    | undefined;
  customFields: LockerverseCustomField[];
  description: string | null;
  fields: LockerverseWidgetFields;
  id: string;
  name: string;
  showCommunityBranding: boolean;
  title: string | null;
};

export type LockerverseSdkErrorCode =
  | "public_events_load_failed"
  | "auction_load_failed"
  | "auction_bid_failed"
  | "auction_purchase_failed"
  | "auction_purchase_status_failed"
  | "auction_receipt_failed"
  | "widget_load_failed"
  | "signup_load_failed"
  | "signup_submit_failed"
  | "total_calculation_failed"
  | "payment_submit_failed"
  | "payment_status_failed"
  | "invalid_selection";

export type LockerverseSdkOperation =
  | "list_public_events"
  | "list_auctions"
  | "load_auction"
  | "create_auction_bid"
  | "create_auction_purchase"
  | "get_auction_purchase_receipt"
  | "get_auction_purchase_status"
  | "load_widget"
  | "load_signup"
  | "submit_signup"
  | "calculate_total"
  | "submit_payment"
  | "get_payment_status";

export type LockerverseSdkServerCode =
  | "PAYMENT_WIDGET_SALES_LIMIT_REACHED"
  | "PAYMENT_WIDGET_PHONE_INVALID"
  | "PAYMENT_WIDGET_PHONE_REQUIRED"
  | "SIGN_UP_WIDGET_PHONE_INVALID"
  | "SIGN_UP_WIDGET_PHONE_REQUIRED";

export type LockerverseSdkFailureKind =
  | "network"
  | "timeout"
  | "aborted"
  | "invalid_response";

export class LockerverseSdkError extends Error {
  readonly failureKind: LockerverseSdkFailureKind | undefined;
  readonly code: LockerverseSdkErrorCode;
  readonly operation: LockerverseSdkOperation;
  readonly reportable: boolean;
  readonly recoveryRecommended: boolean;
  readonly sdkVersion: string;
  readonly serverCode: LockerverseSdkServerCode | undefined;
  readonly status: number | undefined;

  constructor({
    failureKind,
    code,
    message,
    operation,
    reportable,
    recoveryRecommended = false,
    serverCode,
    status,
  }: {
    failureKind?: LockerverseSdkFailureKind | undefined;
    code: LockerverseSdkErrorCode;
    message: string;
    operation: LockerverseSdkOperation;
    reportable: boolean;
    recoveryRecommended?: boolean;
    serverCode?: LockerverseSdkServerCode;
    status?: number | undefined;
  }) {
    super(message);
    this.name = "LockerverseSdkError";
    this.failureKind = failureKind;
    this.code = code;
    this.operation = operation;
    this.reportable = reportable;
    this.recoveryRecommended = recoveryRecommended;
    this.sdkVersion = LOCKERVERSE_SDK_VERSION;
    this.serverCode = serverCode;
    this.status = status;
  }
}

export type LockerverseCheckoutLineItem = {
  productId: string;
  productName: string;
  listingId: string;
  listingSlug: string;
  acceptsDiscounts: boolean;
  eventId: string | null;
  fulfillmentType: LockerverseFulfillmentType;
  pricingMode: "fixed" | "custom";
  paymentOption: LockerversePaymentOption;
  quantity: number;
  unitAmountCents: number;
  subtotalCents: number;
};

export type LockerverseAppliedDiscount = {
  percentageOff?: number | undefined;
  amountOffCents?: number | undefined;
  message?: string | undefined;
  source: "email_list" | "promo_code";
};

export type LockerverseCheckoutAmounts = {
  amountCents: number;
  discountableSubtotalCents: number;
  discountCents: number;
  serviceFeeCents: number;
  subtotalCents: number;
  tipCents: number;
  totalCents: number;
};

export type LockerverseCheckoutQuote = LockerverseCheckoutAmounts & {
  appliedDiscount: LockerverseAppliedDiscount | null;
  authoritative: true;
  paymentReference: string;
  payment: LockerversePaymentConfiguration;
  paymentOption: LockerversePaymentOption;
  lineItems: LockerverseCheckoutLineItem[];
};

export type LockerverseCheckoutEstimate = LockerverseCheckoutAmounts & {
  appliedDiscount: null;
  authoritative: false;
  items: readonly LockerverseCheckoutItem[];
  lineItems: readonly LockerverseCheckoutLineItem[];
  paymentOption: LockerversePaymentOption;
};

export type LockerverseCheckoutPricingInput = {
  email?: string;
  includeServiceFee?: boolean;
  promoCode?: string;
  tipCents?: number;
};

export type LockerversePaymentStatus =
  | "action_required"
  | "failed"
  | "pending"
  | "success";

export type LockerversePaymentResult = {
  checkoutId: string;
  paymentReference: string;
  checkoutStatus: LockerversePaymentStatus;
  retryUrl?: string | undefined;
  clientSecret: string | null;
  paymentRequired: boolean;
  requiresAction: boolean;
  status: string | null;
};

export type LockerversePaymentCompletion = LockerversePaymentResult & {
  email: string | null;
  lineItems: readonly LockerverseCheckoutLineItem[] | null;
};

type AssertPaymentContract<T extends true> = T;
type _PaymentResultMatchesGenerated = AssertPaymentContract<
  LockerversePaymentResult extends GeneratedPaymentResult
    ? GeneratedPaymentResult extends LockerversePaymentResult
      ? true
      : false
    : false
>;

export type LockerverseShipping = {
  address: {
    city: string;
    country: string;
    line1: string;
    line2?: string;
    postal_code: string;
    state: string;
  };
  name: string;
};

export type LockerversePaymentSubmission = {
  confirmationToken?: string;
  paymentMethodConfiguration?: "explicit";
  customFieldAnswers?: LockerverseCustomFieldAnswer[];
  email: string;
  memberId?: string;
  merchSize?: "XS" | "S" | "M" | "L" | "XL" | "XXL" | "XXXL";
  metadata?: Record<string, string>;
  phone?: string;
  shipping?: LockerverseShipping;
};

export type LockerverseCheckoutItem =
  | { productId: string; quantity: number }
  | { productId: string; amountCents: number }
  | { productId: string };

export type LockerverseCheckoutSelectionItem =
  | { productSlug: string; quantity: number }
  | { productSlug: string; amountCents: number }
  | { productSlug: string };

export type LockerverseEstimateCheckoutInput =
  LockerverseCheckoutPricingInput & {
    selection: LockerverseCheckoutSelectionItem[];
  };

export type LockerverseLoadCheckoutInput = {
  selection: LockerverseCheckoutSelectionItem[];
};

export type LockerverseCalculateTotalInput = LockerverseCheckoutPricingInput & {
  catalog: LockerverseCatalog;
} & (
    | {
        items: LockerverseCheckoutItem[];
        selection?: never;
      }
    | {
        items?: never;
        selection: LockerverseCheckoutSelectionItem[];
      }
  );

export type LockerversePreparedCheckout = Readonly<
  LockerverseCheckoutQuote & {
    items: readonly LockerverseCheckoutItem[];
    pricing: Readonly<LockerverseCheckoutPricingInput>;
  }
>;

export type LockerverseLoadedCheckout = Readonly<{
  catalog: LockerverseCatalog;
  checkout: LockerversePreparedCheckout;
}>;

export type LockerverseSubmitPaymentInput = {
  checkout: LockerversePreparedCheckout;
  submission: LockerversePaymentSubmission;
};

type CreateLockerverseWidgetBaseOptions = {
  communitySlug: string;
  fetch?: typeof globalThis.fetch;
  onError?: (error: LockerverseSdkError) => void;
  requestTimeoutMs?: number;
  sentryDsn?: string | null;
  widgetSlug: string;
};

export type LockerverseResourceEnvironmentOptions =
  | {
      apiBaseUrl: string;
      environment: LockerverseEnvironment;
    }
  | {
      apiBaseUrl?: undefined;
      environment?: "production";
    };

export type CreateLockerverseWidgetOptions =
  CreateLockerverseWidgetBaseOptions & LockerverseResourceEnvironmentOptions;

export type LockerverseCheckoutClient = {
  calculateTotal: (
    input: LockerverseCalculateTotalInput
  ) => Promise<LockerversePreparedCheckout>;
  getPaymentStatus: (
    paymentReference: string
  ) => Promise<LockerversePaymentResult>;
  load: {
    (): Promise<LockerverseCatalog>;
    (input: LockerverseLoadCheckoutInput): Promise<LockerverseLoadedCheckout>;
  };
  submitPayment: (
    input: LockerverseSubmitPaymentInput
  ) => Promise<LockerversePaymentResult>;
};
